// src/api/api.js
import axios from 'axios';

const E_CONSENT_SERVICE_URL = process.env.REACT_APP_E_CONSENT_SERVICE_URL;
const E_CONSENT_API_KEY = process.env.REACT_APP_E_CONSENT_API_KEY;

const convertBirthDate = (birthDate) => {
    if (!birthDate || birthDate.length !== 8) {
        return birthDate;
    }
    const year = parseInt(birthDate.substring(0, 4));
    const monthDay = birthDate.substring(4); // MMDD part

    // Convert to Buddhist Era by adding 543 years
    const buddhistYear = year + 543;

    return `${buddhistYear}${monthDay}`;
};

export const api = {
    createUser: async (userData) => {
        return { success: true, user: userData, message: "User created (placeholder)" };
    },

    verifyCustomerInfoDopa: async (customerInfo) => {
        try {
            const response = await axios.post(
                `${E_CONSENT_SERVICE_URL}api/v1/consent/verify-customer-info`,
                {
                    first_name: customerInfo.firstName,
                    last_name: customerInfo.lastName,
                    birth_date: convertBirthDate(customerInfo.dob),
                    id_card: customerInfo.idCardNumber,
                    laser: customerInfo.idCardBackNumber,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': `${E_CONSENT_API_KEY}`, // Your API Key
                    },
                }
            );
            return response.data;
        } catch (error) {
            if (error.response) {
                const errorMessage = error.response.data.message || error.response.data.error || 'Server error during customer info creation';
                const err = new Error(errorMessage);
                err.statusCode = error.response.status; // Add status code to the error object
                throw err;
            } else if (error.request) {
                // The request was made but no response was received
                throw new Error('No response from server. Please check your network connection.');
            } else {
                // Something else happened while setting up the request
                throw new Error('Error setting up request: ' + error.message);
            }
        }
    },

    getUncheckConsentDetails: async (phoneNumber, consentType, consentId, companyId, source) => {
        try {
            const response = await axios.post(
                `${E_CONSENT_SERVICE_URL}api/v1/consent/get-uncheck-consent`,
                {
                    subject: phoneNumber,
                    consent_type: consentType,
                    consent_id: [consentId],
                    company_id: companyId,
                    source: source
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': `${E_CONSENT_API_KEY}`,
                    },
                }
            );

            if (response.data.status === false && Array.isArray(response.data.data)) {
                const parsedConsents = response.data.data.map(item => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(item.Content, 'text/html');
                    const titleElement = doc.querySelector('p strong'); // Get text from first <p><strong>
                    const title = titleElement ? titleElement.textContent.trim() : `Consent ${item.ConsentId}`; // Fallback if no strong tag
                    // Assuming all consents retrieved from "get/uncheck" are 'general' types.
                    // If backend provides a type or there's a rule (e.g., by ConsentId range), this needs adjustment.
                    const type = 'general'; // Defaulting to 'general' - IMPORTANT: Adjust if sensitive consents are mixed in.

                    return {
                        id: String(item.ConsentId), // Ensure ID is a string for consistency
                        title: title,
                        type: type,
                        fullContent: item.Content // Keep full content if needed elsewhere
                    };
                });
                return { success: true, data: parsedConsents };
            } else {
                // If API returns success: false or data is not an array, treat as an error
                throw new Error(response.data.message || 'Failed to fetch consent details: Unexpected API response format.');
            }
        } catch (error) {
            if (error.response) {
                // Access error message from backend response if available
                const errorMessage = error.response.data.message || error.response.data.error || 'Server error during customer info creation';
                const err = new Error(errorMessage);
                err.statusCode = error.response.status; // Add status code to the error object
                throw err;
            } else if (error.request) {
                // Request was made but no response
                throw new Error('No response from consent details API. Please check network connection.');
            } else {
                // Other errors
                throw new Error('Error setting up consent details request: ' + error.message);
            }
        }
    },
    acceptConsent: async (submissionPayload) => {
        try {
            const response = await axios.post(
                `${E_CONSENT_SERVICE_URL}api/v1/consent/accept-consent`, // API endpoint for accepting consents
                submissionPayload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': `${E_CONSENT_API_KEY}`,
                    },
                }
            );
            return { success: response.data.status === true, message: response.data.message, data: response.data };
        } catch (error) {
            if (error.response) {
                const errorMessage = error.response.data.message || error.response.data.error || 'Server error during consent acceptance';
                const err = new Error(errorMessage);
                err.statusCode = error.response.status; // Add status code to the error object
                throw err;
            } else if (error.request) {
                throw new Error('No response from consent acceptance API. Please check network connection.');
            } else {
                throw new Error('Error setting up consent acceptance request: ' + error.message);
            }
        }
    },
    insertCustomerInfo: async (submissionCustomerInfoPayload) => {
        try {
            const response = await axios.post(
                `${E_CONSENT_SERVICE_URL}api/v1/consent/create-consent-cust-info`, // Adjusted path
                submissionCustomerInfoPayload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': `${E_CONSENT_API_KEY}`,
                    },
                }
            );
            return { success: response.data.status === true, message: response.data.message, data: response.data };
        } catch (error) {
            if (error.response) {
                const errorMessage = error.response.data.message || error.response.data.error || 'Server error during customer info creation';
                const err = new Error(errorMessage);
                err.statusCode = error.response.status; // Add status code to the error object
                throw err;
            } else if (error.request) {
                throw new Error('No response from customer info creation API. Please check network connection.');
            } else {
                throw new Error('Error setting up customer info creation request: ' + error.message);
            }
        }
    }
};
